import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter } from 'react-router-dom';
import {
  BrandingProvider,
  companyApi,
  ConfirmProvider,
  createQueryClient,
  initSupabaseConfig,
  preloadBranding,
  renderBootError,
} from '@l2u/web-shared';
import { CompanyAuthProvider } from './lib/auth/company.tsx';
import { App } from './App.tsx';
import './index.css';

// Each SPA app owns its own QueryClient cache instance.
const queryClient = createQueryClient();

const root = document.getElementById('root');
if (!root) throw new Error('Root element missing');

// Phase 3b boot gate — see apps/admin/src/main.tsx for the full rationale.
// The magic-link callback is safe behind this gate: the company client (the
// only one with detectSessionInUrl) is constructed lazily AFTER the gate
// resolves, and the URL hash it reads survives until then.
//
// The tenant's brand is preloaded alongside the config so the first paint is
// already in the tenant's colours — see apps/admin/src/main.tsx.
Promise.all([initSupabaseConfig(), preloadBranding()]).then(
  ([, preloaded]) => {
    createRoot(root).render(
      <StrictMode>
        <QueryClientProvider client={queryClient}>
          {/* companyApi's token store is shared with driverApi (both use
              getCompanyAccessToken), so this authed branding load covers driver
              sessions too. */}
          <BrandingProvider api={companyApi} preloaded={preloaded}>
            <BrowserRouter>
              <CompanyAuthProvider>
                <ConfirmProvider>
                  <App />
                </ConfirmProvider>
              </CompanyAuthProvider>
            </BrowserRouter>
          </BrandingProvider>
        </QueryClientProvider>
      </StrictMode>,
    );
  },
  (err: unknown) => renderBootError(root, err),
);
